import {Component, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { FuseAlertType } from '@fuse/components/alert';
import {AuthService} from '../../../services/auth.service';
import {environment} from '../../../../environments/environment';
import {FuseConfirmationConfig, FuseConfirmationService} from '../../../../@fuse/services/confirmation';
import {first, Observable, of, Subject, switchMap, take, takeUntil} from 'rxjs';
import {
    AuthenticatedResult,
    OpenIdConfiguration,
    UserDataResult
} from 'angular-auth-oidc-client';
import {IcDataStudioScriptsService} from "../../../services/ic-data.studio-scripts.service";
import {IcDataService} from "../../../services/ic-data.service";
import {IcDataRealTimeService} from "../../../services/ic-data.real-time.service";
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {DialogData, TermsDialogComponent} from "../sign-up/sign-up.component";
import {UserService} from "../../../services/user.service";
import {AACUser} from "../../../models/AAC_User.model";
import {AACDataService} from "../../../services/aac-data.service";
import {AACBusinessUnit} from "../../../models/AAC_BusinessUnit.model";


@Component({
    selector     : 'auth-sign-in',
    templateUrl  : './sign-in.component.html',
    encapsulation: ViewEncapsulation.None,
    animations   : fuseAnimations
})
export class AuthSignInComponent implements OnInit, OnDestroy
{
    @ViewChild('signInNgForm') signInNgForm: NgForm;

    alert: { type: FuseAlertType; message: string } = {
        type   : 'success',
        message: ''
    };
    showAlert = false;
    configurations: OpenIdConfiguration[];
    userData$: Observable<UserDataResult>;
    isAuthenticated$: Observable<AuthenticatedResult>;
    isloginactive =false;
    user: AACUser;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        public _authService: AuthService,
        private _router: Router,
        private _fuseConfirmationService: FuseConfirmationService,
        public dialog: MatDialog,
        private _userService: UserService,
        private _aacData: AACDataService
    )
    {
        console.log('Running version ' + environment.appVersion);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Create the form
        this.configurations = this._authService.oidcSecurityService.getConfigurations();
        this.userData$ = this._authService.oidcSecurityService.userData$;
        this.isAuthenticated$ = this._authService.oidcSecurityService.isAuthenticated$;
        this.showAlert = false;
        this._userService.user$
            .pipe((takeUntil(this._unsubscribeAll)))
            .subscribe((user: AACUser) => {
                if (user) {
                    this.user = user;
                }
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    displayMessage(msg: string, msgType: string, timeout: number): void{
        this.alert.message = msg;
        if(msgType === 'error'){
            this.alert.type = 'error';
        }
        else{
            this.alert.type = 'warn';
        }
        this.showAlert=true;
        if(timeout > 1000){
            setTimeout(() => {
                this.showAlert=false;
            }, timeout);
        }
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------
    showRegistration(): void{
        const regConfirmConfig: FuseConfirmationConfig = {
            title: 'CXone BU Not Registered',
            message: 'Your CXone Business Unit is not registered to use the Skybox Advanced Administrator. <span class="font-medium">Would you Like to Register?</span>',
            icon: {
                show: true,
                name: 'heroicons_outline:exclamation',
                color: 'warn'
            },
            actions: {
                confirm: {
                    show: true,
                    label: 'Register',
                    color: 'warn'
                },
                cancel: {
                    show: true,
                    label: 'Cancel'
                }
            },
            dismissible: true
        };
        const dialogRef = this._fuseConfirmationService.open(regConfirmConfig);
        dialogRef.afterClosed().subscribe((result) => {
            if (result === 'confirmed') {
                this._router.navigateByUrl('/sign-up').then();
            }
        });
    }
    showInactiveBU(): void{
        const regConfirmConfig: FuseConfirmationConfig = {
            title: 'CXone BU Not Active',
            message: 'Your CXone Business Unit has been deactivated.  Please contact Skybox Technologies by clicking the Contact Support link below.',
            icon: {
                show: true,
                name: 'heroicons_outline:exclamation',
                color: 'warn'
            },
            actions: {
                confirm: {
                    show : true,
                    label: 'OK',
                    color: 'warn'
                },
                cancel : {
                    show : false,
                    label: 'Cancel'
                }
            },
            dismissible: true
        };
        this._fuseConfirmationService.open(regConfirmConfig);
    }
    showUserNotRegistered(): void{
        let alertMessage = 'Your user is not registered with this CXone Business Unit.  Please contact a registered admin to give you access:';
        let adminCount = 0;
        for (const rUser of this._authService.businessUnit.aacAdmins) {
            alertMessage += '<br>' + rUser;
            adminCount++;
            if(adminCount === 6)
            {break;}
        }
        const regConfirmConfig: FuseConfirmationConfig = {
            title: 'CXone User Not Registered',
            message: alertMessage,
            icon: {
                show: true,
                name: 'heroicons_outline:exclamation',
                color: 'warn'
            },
            actions: {
                confirm: {
                    show : true,
                    label: 'OK',
                    color: 'warn'
                },
                cancel : {
                    show : false,
                    label: 'Cancel'
                }
            },
            dismissible: true
        };
        this._fuseConfirmationService.open(regConfirmConfig);
    }
    /**
     * login
     */

    login(isFedramp: boolean): void {
        const popupOptions = { width: 420, height: 650, left: 50, top: 50 };
        this._authService.oidcConfigId = 'CXone-Standard';
        if (isFedramp) {
            this._authService.oidcConfigId = 'CXone-Fedramp';
        }
        if(!this.isloginactive) {
            this.isloginactive = true;
            const authOptions = {};
            this._authService.oidcSecurityService.authorizeWithPopUp(authOptions, popupOptions, this._authService.oidcConfigId).pipe(first())
                .subscribe(({isAuthenticated, userData, accessToken, errorMessage}) => {
                    if (errorMessage) {
                        if(errorMessage === 'User closed popup'){
                            this.displayMessage('User closed CXone login window.', 'warn', 3000);
                        }
                        else if(errorMessage === 'Error: no code in url'){
                            this.displayMessage('Password is either locked or incorrect.', 'warn', 3000);
                        }
                        else{
                            this.displayMessage(errorMessage, 'warn', 2000);
                        }
                    }
                    if (isAuthenticated) {
                        this._authService.checkUser().pipe(first()).subscribe((aacAuthResponse) => {
                            if (aacAuthResponse.isAuthenticated) {
                                // check service level and display message
                                if(this.user.aacServiceLevel === 'PRE-TRIAL'){
                                    this.openTrialTermsDialog(true);
                                }
                                else if(this.user.aacServiceLevel === 'TRIAL'){
                                    this.openTrialTermsDialog(false);
                                }

                                // Navigate to the redirect url
                                const redirectURL = this._activatedRoute.snapshot.queryParamMap.get('redirectURL') || 'signed-in-redirect';
                                this._router.navigateByUrl(redirectURL).then();
                            } else {
                                switch (aacAuthResponse.message) {
                                    case 'BU Not Registered':
                                        this.showRegistration();
                                        break;
                                    case 'Agent ID is not registered':
                                        this.showUserNotRegistered();
                                        break;
                                    case 'BU Inactive':
                                        this.showInactiveBU();
                                        break;
                                    default:
                                        this.displayMessage(aacAuthResponse.message + '  Please contact Skybox Technologies with any questions by clicking either the Contact Support or Subscription Inquiry links below.  ', 'error', 10000);
                                        break;
                                }
                            }
                        });
                    }
                    this.isloginactive = false;
                });
        }
    }


    studioTokenLogin() {
        navigator.clipboard.readText().then((studioToken) =>
            {
                {

                    const tokenArray = studioToken.split('::');
                    const clipboardAuthType=tokenArray[0];
                    const versionNice = tokenArray[1];
                    const token = tokenArray[2];
                    const baseURL = tokenArray[3];
                    const trueFalseBit = tokenArray[4];
                    sessionStorage['StudioAuth'] = token;
                    this.isloginactive = true;
                    this._authService.checkUser().pipe(first()).subscribe((aacAuthResponse) => {
                            if (aacAuthResponse.isAuthenticated) {

                                // Navigate to the redirect url
                                const redirectURL = this._activatedRoute.snapshot.queryParamMap.get('redirectURL') || 'signed-in-redirect';
                                // this._router.navigate([redirectURL]);
                                this._router.navigateByUrl(redirectURL).then();
                            } else {
                                switch (aacAuthResponse.message) {
                                    case 'BU Not Registered':
                                        this.showRegistration();
                                        break;
                                    case 'User Not Registered':
                                        this.showUserNotRegistered();
                                        break;
                                    case 'BU Inactive':
                                        this.showInactiveBU();
                                        break;
                                    default:
                                        this.displayMessage(aacAuthResponse.message, 'error', 10000);
                                        break;
                                }
                            }
                        this.isloginactive = false;
                        }
                    );
                }
            })
            .catch((error) =>{
                sessionStorage['StudioAuth'] = '';
                this._authService.clearStudioAuth();
                this.alert.message = 'Invalid Token. Please Retry';
                this.alert.type = 'warn';
                this.showAlert=true;
            });
    }
    logOutSession(): void{
        this._authService.signOut();
    }
    openTrialTermsDialog(isPreTrial: boolean): void {
        let terms: string;
        if(isPreTrial){
            terms = 'Thank you for being part of the Skybox Advanced Administrator Console Beta program.';
            terms += '  Skybox Technologies is moving to a subscription model.';
            terms += '  Business Unit ' + this.user.aacBusinessUnit + ' (' + this.user.aacBusinessUnitName + ') is now set up for a trial period of ' + this.user.aacBusinessUnitTrialDays + ' days.';
            terms += '  By acknowledging, you understand that you will be dealing with live Business Unit data.';
            terms += '  Skybox Technologies is not responsible for any issues to the Business Unit due to data changes made from this application.';
            terms += '  Please contact Skybox Technologies at any time to ';
        }
        else{
            terms = 'The AAC registration for Business Unit ' + this.user.aacBusinessUnit + ' (' + this.user.aacBusinessUnitName + ') is set up for a trial period of ' + this.user.aacBusinessUnitTrialDays + ' days.';
            terms += '  There are ' + this.user.aacBusinessUnitTrialDaysRemaining + ' days remaining in this trial.';
            terms += '  At the end of the trial period, you will no longer have access to this account.  Please contact Skybox Technologies at any time to ';
        }

        const dialogRef = this.dialog.open(TrialDialogComponent, {
            width: '550px',
            disableClose: true,
            data: {text: terms, businessUnitName: this.user.aacBusinessUnitName, businessUnit: this.user.aacBusinessUnit, name: this.user.aacFirstName + ' ' + this.user.aacLastName, serviceLevel: this.user.aacServiceLevel}
        });

        dialogRef.afterClosed().subscribe((result) => {
            if(result){
                const aacBusinessUnit = new AACBusinessUnit(this.user);
                aacBusinessUnit.serviceLevel = 'TRIAL';
                aacBusinessUnit.isActive = true;
                this._aacData.updateRegisterBusinessUnit(aacBusinessUnit,
                    this.user.aacUserName).subscribe( (result)=>{
                    if (result.aacBusinessUnit && result.aacBusinessUnit.id)
                    {
                        this.user.aacServiceLevel = result.aacBusinessUnit.serviceLevel;
                        this._userService.user = this.user;
                    }
                });
            }
        });
    }
}

@Component({
    selector: 'trial-dialog',
    templateUrl: 'trial-dialog.html',
})
export class TrialDialogComponent {

    constructor(
        public dialogRef: MatDialogRef<TrialDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: DialogData) {
    }

    onNoClick(): void {
        this.dialogRef.close();
    }
}
